import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';

const ACTION_LABELS = {
  create: 'Oprettet',
  update: 'Opdateret',
  delete: 'Slettet',
  delete_all: 'Slettet alle',
};

const ENTITY_LABELS = {
  voucher: 'Bilag',
  account: 'Konto',
  attachment: 'Vedhæftning',
};

export default function AuditLog() {
  const { apiFetch } = useAuth();
  const [entries, setEntries] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [expanded, setExpanded] = useState(null);
  const pageSize = 50;

  const load = () => {
    apiFetch(`/api/settings/audit-log?limit=${pageSize}&offset=${page * pageSize}`)
      .then(data => {
        setEntries(data.entries);
        setTotal(data.total);
      })
      .catch(() => {});
  };

  useEffect(() => { load(); }, [page]);

  const toggleExpand = (id) => {
    setExpanded(expanded === id ? null : id);
  };

  const formatData = (data) => {
    if (!data) return null;
    try {
      const obj = typeof data === 'string' ? JSON.parse(data) : data;
      return JSON.stringify(obj, null, 2);
    } catch {
      return String(data);
    }
  };

  const totalPages = Math.ceil(total / pageSize);

  return (
    <div>
      <div className="page-header">
        <h1>Ændringslog</h1>
        <span style={{color: 'var(--gray-500)', fontSize: '0.875rem'}}>{total} registreringer</span>
      </div>

      <div className="alert alert-success" style={{background: '#ebf8ff', color: '#2a4365', border: '1px solid #90cdf4'}}>
        Alle ændringer i bilag og konti logges her. Denne log kan ikke redigeres eller slettes.
      </div>

      <div className="table-container">
        <table>
          <thead>
            <tr>
              <th>Tidspunkt</th>
              <th>Bruger</th>
              <th>Handling</th>
              <th>Type</th>
              <th>ID</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {entries.length === 0 ? (
              <tr><td colSpan="6" className="text-center">Ingen ændringer registreret endnu.</td></tr>
            ) : entries.map(entry => (
              <React.Fragment key={entry.id}>
                <tr style={{cursor: 'pointer'}} onClick={() => toggleExpand(entry.id)}>
                  <td style={{whiteSpace: 'nowrap', fontSize: '0.8rem'}}>
                    {new Date(entry.created_at).toLocaleString('da-DK')}
                  </td>
                  <td>{entry.user_name || 'System'}</td>
                  <td>
                    <span className={`badge ${entry.action === 'delete' || entry.action === 'delete_all' ? 'badge-inactive' : 'badge-active'}`}>
                      {ACTION_LABELS[entry.action] || entry.action}
                    </span>
                  </td>
                  <td>{ENTITY_LABELS[entry.entity_type] || entry.entity_type}</td>
                  <td>{entry.entity_id || '-'}</td>
                  <td className="text-right" style={{fontSize: '0.8rem', color: 'var(--gray-500)'}}>
                    {expanded === entry.id ? 'Skjul' : 'Vis detaljer'}
                  </td>
                </tr>
                {expanded === entry.id && (
                  <tr>
                    <td colSpan="6" style={{background: 'var(--gray-50)', padding: '1rem'}}>
                      <div style={{display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem'}}>
                        {entry.old_data && (
                          <div>
                            <strong style={{fontSize: '0.8rem', color: 'var(--danger)'}}>Tidligere data:</strong>
                            <pre style={{fontSize: '0.75rem', background: '#fff5f5', padding: '0.5rem', borderRadius: '4px', overflow: 'auto', maxHeight: '200px', whiteSpace: 'pre-wrap'}}>
                              {formatData(entry.old_data)}
                            </pre>
                          </div>
                        )}
                        {entry.new_data && (
                          <div>
                            <strong style={{fontSize: '0.8rem', color: 'var(--success)'}}>Nye data:</strong>
                            <pre style={{fontSize: '0.75rem', background: '#f0fff4', padding: '0.5rem', borderRadius: '4px', overflow: 'auto', maxHeight: '200px', whiteSpace: 'pre-wrap'}}>
                              {formatData(entry.new_data)}
                            </pre>
                          </div>
                        )}
                        {!entry.old_data && !entry.new_data && (
                          <div style={{color: 'var(--gray-500)', fontSize: '0.85rem'}}>Ingen detaljer tilgængelige.</div>
                        )}
                      </div>
                    </td>
                  </tr>
                )}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>

      {totalPages > 1 && (
        <div style={{display: 'flex', justifyContent: 'center', gap: '0.5rem', marginTop: '1rem'}}>
          <button className="btn btn-secondary btn-sm" disabled={page === 0} onClick={() => setPage(p => p - 1)}>Forrige</button>
          <span style={{padding: '0.35rem 0.75rem', fontSize: '0.85rem'}}>Side {page + 1} af {totalPages}</span>
          <button className="btn btn-secondary btn-sm" disabled={page >= totalPages - 1} onClick={() => setPage(p => p + 1)}>Næste</button>
        </div>
      )}
    </div>
  );
}
